"use client";
import { useEffect, useState, useMemo } from "react";
import { useSession } from "session";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  type TravelBookingFormValues,
  travelBookingSchema,
} from "../lib/schemas/travel-booking";
import { Button } from "@/components/ui/button";
import { FormProvider } from "react-hook-form";
import { DestinationStep } from "../steps/destination-step";
import { CustomerStep } from "../steps/customer-step";
import { DeliveryStep } from "../steps/delivery-step";
import { CalculationPanel } from "../steps/calculation-panel";
import Stepper from "components/Stepper/newstepper";
import { ProductModal } from "../steps/product-modal";
import { useMutation } from "react-query";
import { adddNewContact, createGenericOrder } from "../apis";
import { DeclarationFields } from "../steps/declaration-fields";
import { buildForexOrderPayload, modeOf } from "../lib/forex-payload";
import { Img, Spinner } from "components";
import { Toaster, toast } from "sonner";
import { Location, useLocation, useNavigate } from "react-router-dom";
import { kycStep, products, step1, step2Singal } from "../lib/FormConfigs";
import Navbar from "components/Navbar/Index";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { setNewGenricOrderID } from "redux/features/CreateOrder";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setForexCouponInfo, setForexOrderType } from "redux/features/CreateGICOrder";
import InvoiceStep from "../steps/invoice-step";
import KYCStep from "../steps/kyc";

export interface GenericOrderLocationState extends Location {
  state: {
    opportunityId?: number;
    contactId?: number;
    name?: string;
    email?: string;
    number?: string;
  }
}


function TravelBookingWizard() {
  const nevigate = useNavigate();
  const dispatch = useAppDispatch();
  // A coupon belongs to the order being placed, never to the next one.
  useEffect(() => {
    dispatch(setForexCouponInfo(null));
  }, []);
  const forexCouponInfo = useAppSelector((state) => state.CreateGICOrder?.forexCouponInfo) as { code?: string } | null;
  const [currentStep, setCurrentStep] = useState(1);
  const [productTypeFromInvoice, setProductTypeFromInvoice] = useState<string>('');
  const [preCalcData, setPreCalcData] = useState<{
    grandTotal: number;
    cgst: number;
    sgst: number;
    roundOff: number;
    igst: number;
    serviceCharge: number;
    amount: number;
    abt: number;
  } | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [, setNewCustomerID] = useState<string>("");
  const [kycVerificationStatus, setKycVerificationStatus] = useState<{
    panVerified: boolean;
    panFailed: boolean;
    passportVerified: boolean;
    passportFailed: boolean;
  }>({
    panVerified: false,
    panFailed: false,
    passportVerified: false,
    passportFailed: false,
  });

  // Track customer kyc_status separately to ensure it's available
  const [customerKycStatus, setCustomerKycStatus] = useState<string | undefined>(undefined);

  const location = useLocation() as GenericOrderLocationState;
  const new_order_type =
    new URLSearchParams(window.location.search).get("type") || "";
  const destinationStep = step1[new_order_type as keyof typeof step1];
  // const customerStep = step2[new_order_type as keyof typeof step2Singal];
  const customerStepSingal =
    step2Singal[new_order_type as keyof typeof step2Singal];
  const productsConfig = products[new_order_type as keyof typeof products];
  const kycSteps = kycStep[new_order_type as keyof typeof kycStep];

  // Create a single form instance that will collect all data
  const form = useForm<TravelBookingFormValues>({
    resolver: zodResolver(travelBookingSchema),
    defaultValues: {
      destination: {
        purposeOfTravel: "",
        destination: "",
        dateRange: {
          start_date: "",
          end_date: "",
        },
      },
      customers: [],
      deliveries: [],
      declarations: { payoutMethod: "bank" },
    },
    mode: "onChange",
  });

  // Watch customer data to track kyc_status
  const customerData = form.watch('customers.0');
  useEffect(() => {
    const kycStatus = (customerData as any)?.kyc_status;
    if (kycStatus) {
      setCustomerKycStatus(kycStatus);
      console.log('Parent: KYC Status updated:', kycStatus);
    }
  }, [customerData]);

  const order_type = form.watch('customers')?.[0]?.products?.[0]?.productType?.type;

  const allSteps = [
    {
      title: "Invoice Details",
      fields: ["invoice"],

      component: <InvoiceStep onDataExtracted={(data) => {
        // if (products?.length) {
        //   console.log(products)
        //   return
        // }
        if (data) {
          setPreCalcData({
            grandTotal: data.total_amount,
            cgst: data.CGST,
            sgst: data.SGST,
            roundOff: data.round_off,
            igst: data.IGST,
            serviceCharge: data.service_charge,
            amount: data.amount.reduce((acc, item) => acc + parseFloat(item), 0),
            abt: data.forex_amount
          })
          if (data?.product_type === 'Sale') {
            setProductTypeFromInvoice('Sale')
            setCurrentStep(2)
          }
          else
            setCurrentStep(2)
        }
        // You can perform additional actions here if needed
      }} form={form} />,
    },
    {
      title: "Customer & Product Details",
      fields: ["customers"],
      component: (
        <>
          <CustomerStep isDataFormInvoice={!!form.getValues("invoice")?.invoiceFile?.name} config={customerStepSingal} form={form} />
          <DeclarationFields form={form} group="customer" />
        </>
      ),
    },
    {
      // A sale is asked where it is going; an encashment, how it is declared and paid out.
      title: order_type === 'sell' || order_type === 'encashment' ? "Encashment Details" : "Destination Details",
      fields: ["destination"],
      component: (
        <>
          <DestinationStep config={destinationStep} form={form} />
          <DeclarationFields form={form} group="destination" />
        </>
      ),
    },
    {
      title: "KYC",
      fields: ["kyc"],
      component: <KYCStep
        form={form}
        config={kycSteps}
        onVerificationStatusChange={setKycVerificationStatus}
      />,
    },
    {
      title: "Delivery",
      fields: ["deliveries"],
      component: (
        <>
          <DeclarationFields form={form} group="payment" />
          <DeliveryStep form={form} />
        </>
      ),
      optional: true,
    },
  ];

  // Both modes see every step; the title and fields change with the mode.
  const steps = useMemo(() => allSteps, [order_type]);

  const step = steps[currentStep - 1];
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === steps.length;
  // const isOptionalStep = step.optional;

  // check validatoion button
  const validateCurrentStep = async () => {
    const fieldsToValidate = step.fields as (keyof TravelBookingFormValues)[];

    if (fieldsToValidate.includes("customers")) {
      const result = await form.trigger(fieldsToValidate);
      const customers = form.getValues("customers") || [];
      const firstCustomer = customers[0] || {};

      const hasFullName = !!firstCustomer.fullName?.trim();
      const hasCustomerNo = !!firstCustomer.mobileNumber?.trim();

      const hasValidProducts =
        Array.isArray(firstCustomer.products) &&
        firstCustomer.products.length > 0 &&
        firstCustomer.products.every(
          (product) =>
            product?.productType?.label &&
            Array.isArray(product.currencies) &&
            product.currencies.length > 0 &&
            product.currencies.every(
              (currency) =>
                typeof currency.amount === "number" &&
                typeof currency.rate === "number"
            )
        );

      let isFirstCustomerValid = hasFullName && hasValidProducts && hasCustomerNo;

      if (!hasFullName) {
        form.setError("customers.0.fullName", {
          type: "manual",
          message: "Full name is required.",
        });
      }

      if (!hasValidProducts) {
        alert("At least one valid product with amount and rate is required.");
        form.setError("customers.0.products", {
          type: "manual",
          message:
            "At least one valid product with amount and rate is required.",
        });
      }

      if (!form.getValues("declarations.residency")) {
        form.setError("declarations.residency", { type: "manual", message: "Residency is required." });
        toast.error("Select the customer's residency.", { id: "forex-residency" });
        return false;
      }

      return result || isFirstCustomerValid;
    }

    if (fieldsToValidate.includes("destination") && modeOf(form.getValues()) === "sale") {
      if (!form.getValues("destination.purposeOfTravel")) {
        form.setError("destination.purposeOfTravel", { type: "manual", message: "Purpose is required." });
        return false;
      }
    }
    const result = await form.trigger(fieldsToValidate);
    return result;
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (isValid) {
      // Helper function to check KYC status and skip if verified
      const checkAndSkipKYC = () => {
        const customer = form.getValues('customers.0');
        const kycStatusFromForm = (customer as any)?.kyc_status;
        const kycStatus = kycStatusFromForm || customerKycStatus;

        // Debug: log the kyc_status value

        if (kycStatus && (String(kycStatus).toLowerCase() === 'active' || String(kycStatus).toLowerCase() === 'verified')) {
          // Find the Delivery step index in filtered steps
          const deliveryStepIndex = steps.findIndex(s => s.title === "Delivery");
          if (deliveryStepIndex !== -1) {
            setCurrentStep(deliveryStepIndex + 1);
            return true; // Indicates KYC was skipped
          }
        }
        return false; // KYC step should be shown
      };

      if (productTypeFromInvoice === 'Sale' && currentStep === 2) {
        // For Sale orders, check KYC status before going to KYC step
        const kycSkipped = checkAndSkipKYC();
        if (!kycSkipped) {
          // Find KYC step in filtered steps
          const kycStepIndex = steps.findIndex(s => s.title === "KYC");
          if (kycStepIndex !== -1) {
            setCurrentStep(kycStepIndex + 1);
          }
        }
        return;
      }

      // Check if we're moving to KYC step and if kyc_status is active or verified
      const kycStepIndex = steps.findIndex(s => s.title === "KYC");
      if (kycStepIndex !== -1 && currentStep === kycStepIndex) {
        const kycSkipped = checkAndSkipKYC();
        if (kycSkipped) {
          return;
        }
      }

      // Move to next step in filtered steps
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      // Show validation errors
    }
  };
  const { mutateAsync: addContactMutateAsync, isLoading: loader } = useMutation({
    mutationFn: adddNewContact,
    onSuccess: () => {

    },
    onError: () => {
    },
  })

  const handleNextWithProduct = async () => {
    const customerFields = [
      "customers.0.fullName",
      "customers.0.email",
      "customers.0.mobileNumber",
      "customers.0.pan",
    ];

    const isProductsAvailable = (form.getValues("customers.0.products")!?.length) > 0;

    // Trigger validation only for those fields
    const results = await Promise.all(
      customerFields.map((field) => form.trigger(field as any))
    );

    const customer = form.getValues("customers")[0];

    // If all validations passed
    if (results.every((result) => result === true)) {
      try {
        if (!customer.customerId) {
          const addContactRes = await addContactMutateAsync({
            name: customer.fullName,
            pan_no: customer.pan as string,
            phone_number: customer.mobileNumber,
            email: customer.email as string,
          });

          form.setValue("customers.0.customerId", addContactRes?.id?.toString());

          const finalLabel = {
            value: addContactRes?.id ? String(addContactRes?.id) : "",
            label: addContactRes?.full_name,
          };

          setNewCustomerID(addContactRes?.id);
          dispatch(setNewGenricOrderID([finalLabel]));
        } else {
          setNewCustomerID(customer.customerId);
        }
        if (!isProductsAvailable) {
          setIsProductModalOpen(true);
        }
      } catch (err) {
      }
    } else {
    }
  };

  useEffect(() => { }, [isProductModalOpen]);
  useEffect(() => {
    const stateData = location?.state
    if (stateData) {
      const { contactId, name, email, number } = stateData;
      if (contactId) {
        form.setValue("customers.0.customerId", contactId.toString());
      }
      if (name) {
        form.setValue("customers.0.fullName", name);
      }
      if (email) {
        form.setValue("customers.0.email", email);
      }
      if (number) {
        form.setValue("customers.0.mobileNumber", number);
      }
    }
  }, [location])

  const handleBack = () => {
    if (productTypeFromInvoice === 'Sale') {
      const kycStepIndex = steps.findIndex(s => s.title === "KYC");
      if (kycStepIndex !== -1 && currentStep === kycStepIndex + 1) {
        setCurrentStep(2);
        return;
      }
    }
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const navigate = useNavigate();

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: createGenericOrder,
    onSuccess: (data) => {
      const orderId = data?.data?.order_id;
      if (orderId) {
        navigate(`/forex/orderdetails/${orderId}`);
        dispatch(setForexOrderType(""));
        form.reset();
        setCurrentStep(1);
        setIsSubmitting(false);
        setIsProductModalOpen(false);
      }
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const handleSubmit = async (values: TravelBookingFormValues) => {
    let payload;
    try {
      payload = buildForexOrderPayload(values, location?.state?.opportunityId, forexCouponInfo?.code);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "The order is not complete.");
      return;
    }
    setIsSubmitting(true);
    // A refusal is shown by the call itself, in core's words.
    await mutateAsync(payload).catch(() => undefined);
  };

  const handleCancel = () => {
    nevigate("/forex/orders");
  };

  return (
    <div className="h-screen overflow-hidden mx-auto p-4">
      {(isLoading || loader) && <Spinner />}
      <Toaster position="top-right" />

      <FormProvider {...form}>
        <form
          className="h-full"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="flex flex-col h-full gap-3">
            {/* <NavbarWithActions header="Create Order" /> */}
            <Navbar>
              <div className=" w-full flex justify-between">
                <div className=" flex gap-3 items-center">
                  <Img
                    src="images/sidebar_img/remit.svg"
                    className="h-5"
                    alt=""
                  />
                  <div className="gap-2 flex flex-col">
                    <span className="text-sm font-semibold">
                      Create Forex Order
                    </span>
                    <span className="text-xs font-normal text-[#666666]">
                      Create a forex transaction
                    </span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="gap-2.5 flex items-center">
                    <Img src="images/overview/wrench.svg" alt="" />
                    <span className="text-sm font-semibold text-blue-700">
                      Help
                    </span>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="rounded-lg py-[11px] px-4 border bg-gray-button-gray-lite border-gray-border-gray-lite cursor-pointer hover:bg-gray-200">
                        Cancel order
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogPortal>
                      <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
                      <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-2xl animate-contentShow">
                        <AlertDialogHeader className="flex text-left gap-4 pt-8 pb-4 px-10 ">
                          <AlertDialogTitle className="font-bold text-xl leading-7 tracking-[2%] text-black-label">
                            Are you absolutely sure?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="font-medium text-base leading-6 tracking-[2%] text-[#666666] ">
                            This action can not be undone. This will permanently
                            delete your progress and remove the actions
                            preformed.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="flex justify-end items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
                          <AlertDialogCancel
                            className=" m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700"
                            asChild
                          >
                            <button>Close</button>
                          </AlertDialogCancel>
                          <AlertDialogAction
                            className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
                            asChild
                          >
                            <button className="" onClick={handleCancel}>
                              Continue
                            </button>
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialogPortal>
                  </AlertDialog>
                </div>
              </div>
            </Navbar>
            <div className="flex sm:flex-col md:flex-col flex-row gap-3 h-full md:h-auto">
              {/* Scrolls inside the card: the declarations make a step taller than the screen. */}
              <div className="bg-white-A700 p-3 rounded-lg w-full h-full md:h-auto overflow-y-auto md:overflow-visible">
                <div className="h-full md:h-auto space-y-2">
                  <h3>
                    <Stepper
                      currentStep={currentStep - 1}
                      steps={steps.map((step) => step.title)}
                    />
                  </h3>
                  <section className="">{step.component}</section>
                  <footer className="flex gap-2 pt-4">
                    <>
                      {!isFirstStep && (
                        <Button
                          className="rounded-md border border-blue-700  text-blue-700"
                          type="button"
                          variant="outline"
                          onClick={handleBack}
                        >
                          Back
                        </Button>
                      )}
                    </>
                    <>
                      {/* {isOptionalStep && (
                        <Button className="rounded-md border border-blue-700 bg-blue-700 text-white-A700" type="button" variant="ghost" onClick={handleSkip}>
                          Skip
                        </Button>
                      )} */}
                      {!isLastStep ? (
                        currentStep === 2 &&
                          (!form.watch("customers.0.products")?.length || !form.watch('customers.0.customerId')) ? (
                          <Button
                            className="rounded-md border border-blue-700 bg-blue-700 text-white-A700"
                            type="button"
                            variant="ghost"
                            onClick={handleNextWithProduct}
                          >
                            Continue to add product
                          </Button>
                        ) : (
                          currentStep === 1 ?
                            <Button
                              className="rounded-md border border-blue-700 bg-blue-700 text-white-A700"
                              type="button"
                              onClick={() => {
                                form.reset()
                                setCurrentStep(2)
                                // handleNext()
                              }}
                            >
                              Proceed without invoice
                            </Button>
                            :
                            <Button
                              className="rounded-md border border-blue-700 bg-blue-700 text-white-A700"
                              type="button"
                              onClick={() => handleNext()}
                              disabled={
                                currentStep === 4 && // KYC step
                                !(
                                  // Both APIs succeeded
                                  (kycVerificationStatus.panVerified && kycVerificationStatus.passportVerified) ||
                                  // PAN succeeded, passport failed
                                  (kycVerificationStatus.panVerified && kycVerificationStatus.passportFailed) ||
                                  // Both APIs failed
                                  (kycVerificationStatus.panFailed && kycVerificationStatus.passportFailed)
                                )
                              }
                            >
                              Next
                            </Button>
                        )
                      ) : (
                        <Button
                          className="rounded-md border border-blue-700 bg-blue-700 text-white-A700"
                          onClick={() => {
                            const customers = form.getValues().customers
                            const filteredCustomers = customers.filter(c => c?.fullName || c.customerId)
                            form.setValue("customers", filteredCustomers);
                            console.log("Form values:", form.formState.errors);
                            form.handleSubmit(handleSubmit)()
                          }}
                          disabled={isSubmitting}
                          type="button"
                        >
                          {isSubmitting ? "Submitting..." : "Submit"}
                        </Button>
                      )}
                    </>
                  </footer>
                  {/* <div className="mt-8 p-4 border rounded-md overflow-auto h-60">
                    <h2 className="text-lg font-semibold mb-2">Current Form Data (Debug):</h2>
                    <pre className="text-xs bg-muted p-2 rounded">
                      {JSON.stringify(form.watch(), null, 2)}
                    </pre>
                  </div> */}
                </div>
              </div>
              <div className={`bg-white-A700 p-3 rounded-lg w-[45%] sm:w-full md:w-full h-full md:h-auto ${form.watch("customers.0.products")?.length ? "" : "md:hidden"}`}>
                <CalculationPanel order_type={order_type as "buy" | "sell"} preCalcData={preCalcData} />
              </div>
            </div>
          </div>
        </form>
        {isProductModalOpen && (
          <ProductModal
            config={productsConfig}
            order_type={form.getValues("order_type")}
            isOpen={isProductModalOpen}
            onClose={() => setIsProductModalOpen(false)}
            onSubmit={async (values) => {
              const currentProducts =
                form.getValues("customers.0.products") || [];
              console.log("Current Products:", currentProducts);
              console.log("New Product Values:", values);
              const updatedProducts = [...currentProducts, values];
              form.setValue("customers.0.products", updatedProducts);
              setIsProductModalOpen(false);
            }}
          />
        )}
      </FormProvider>

      {/* Debug panel to show current form values */}
    </div>
  );
}

/**
 * Core refuses a forex order from anyone without the forex desk permission, so the wizard is not
 * offered to them: they get a plain message instead of a form that fails at the last step.
 */
export default function TravelBookingPage() {
  const { can } = useSession();
  const isForex = new URLSearchParams(window.location.search).get("type") === "forex";
  if (isForex && !can("forex.desk")) {
    return (
      <div className="m-3 rounded-lg bg-white-A700 p-6 text-sm text-[#333333]" role="alert">
        <h1 className="mb-2 text-lg font-semibold">You cannot create a forex order</h1>
        <p>Your role does not include creating forex orders. Ask a manager if you need this.</p>
      </div>
    );
  }
  return <TravelBookingWizard />;
}
